import { inject, Injectable, signal } from '@angular/core';
import {
    DeliveryDate,
    Product,
    Variant,
} from '../components/hof/product/product.component';
import { ToastrService } from 'ngx-toastr';
import { DOMHandlerService } from './domhandler.service';
import { UmamiService } from './umami.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class CartService {
    itemsMap = signal<CartItemMap>({});
    showCart = signal(false);
    itemCount = signal<number>(0);
    cartItems = signal<CartItem[]>([]);
    umami = inject(UmamiService);
    private toastService = inject(ToastrService);
    private domhandler = inject(DOMHandlerService);
    private translationService = inject(TranslateService);

    constructor() {
        this.loadCart();
        this.updateItemCount();
        this.updateCartItems();
    }

    calculatePrice(item: CartItem): number {
        if (item.product.inventoryType === 'weight') {
            return (
                (item.product.price / 1000) *
                item.selectedVariant.weight *
                item.amount
            );
        }
        return item.product.price * item.amount;
    }

    toggleCart() {
        this.showCart.set(!this.showCart());
    }

    closeCart() {
        this.showCart.set(false);
    }

    /**
     * Descreases the amount of a cart item in the cart
     * @param cartItem - the cart item to decrease the amount of
     * @returns void
     */
    removeFromCart(cartItem: CartItem) {
        if (
            this.itemsMap()[this.createItemKey(cartItem)] &&
            this.itemsMap()[this.createItemKey(cartItem)].amount > 0
        ) {
            this.itemsMap()[this.createItemKey(cartItem)].amount--;
            if (this.itemsMap()[this.createItemKey(cartItem)].amount === 0) {
                delete this.itemsMap()[this.createItemKey(cartItem)];
            }
            this.saveAndLoadCart();
            this.umami.track(
                'remove-from-cart-click',
                this.umami.getCartItemPayload(cartItem),
            );
        }
    }

    removeNFromCart(cartItem: CartItem, n: number) {
        if (this.itemsMap()[this.createItemKey(cartItem)]) {
            this.itemsMap()[this.createItemKey(cartItem)].amount -= n;
            if (this.itemsMap()[this.createItemKey(cartItem)].amount <= 0) {
                delete this.itemsMap()[this.createItemKey(cartItem)];
            }
            this.saveAndLoadCart();
            this.umami.track(
                'remove-from-cart-click',
                this.umami.getCartItemPayload(cartItem),
            );
        }
    }

    /**
     * Deletes a cart item from the cart
     * @param cartItem - the cart item to delete
     * @returns void
     */
    deleteFromCart(cartItem: CartItem) {
        if (this.itemsMap()[this.createItemKey(cartItem)]) {
            delete this.itemsMap()[this.createItemKey(cartItem)];
            this.saveAndLoadCart();
        } else {
            //the key has changed, so we need to find the item in the cart
            const itemKey = Object.keys(this.itemsMap()).find((key) => {
                const item = this.itemsMap()[key];
                return (
                    item.product.documentId === cartItem.product.documentId &&
                    item.selectedVariant.uuid ===
                        cartItem.selectedVariant.uuid &&
                    item.selectedDeliveryDate?.documentId ===
                        cartItem.selectedDeliveryDate?.documentId
                );
            });
            if (itemKey) {
                delete this.itemsMap()[itemKey];
                this.saveAndLoadCart();
            }
        }
        this.umami.track(
            'delete-from-cart-click',
            this.umami.getCartItemPayload(cartItem),
        );
    }

    clearCart() {
        this.itemsMap.set({});
        this.saveAndLoadCart();
    }

    addToCart(cartItem: CartItem, mouseEvent?: MouseEvent) {
        const itemKey = this.createItemKey(cartItem);

        const differentDeliveryDatesAmount = new Set(
            this.cartItems()
                .concat(cartItem)
                .filter((item) => item.selectedDeliveryDate)
                .map((item) => item.selectedDeliveryDate?.documentId),
        ).size;
        if (differentDeliveryDatesAmount > 3) {
            this.translationService
                .get('product.maxDifferentDeliverydates')
                .subscribe((translation) => {
                    this.toastService.error(translation);
                });
            return;
        }

        if (this.itemsMap()[itemKey]) {
            if (this.itemsMap()[itemKey].amount + cartItem.amount > 50) {
                this.translationService
                    .get('product.maxAmountOfVariant', {
                        amount: 50,
                    })
                    .subscribe((translation) => {
                        this.toastService.error(translation);
                    });
                return;
            }

            if (
                this.itemsMap()[itemKey].amount + cartItem.amount >
                cartItem.selectedVariant.amount
            ) {
                this.translationService
                    .get('product.maxAmountOfVariant', {
                        amount: cartItem.selectedVariant.amount,
                    })
                    .subscribe((translation) => {
                        this.toastService.error(translation);
                    });
                return;
            }

            this.itemsMap()[itemKey].amount += cartItem.amount;
        } else {
            this.itemsMap()[itemKey] = cartItem;
        }

        this.domhandler.spawnAddToCartAnimation(mouseEvent, cartItem.amount);
        this.saveAndLoadCart();
        this.umami.track(
            'add-to-cart-click',
            this.umami.getCartItemPayload(cartItem),
        );
    }

    private saveCart() {
        localStorage.setItem('cart', JSON.stringify(this.itemsMap()));
    }

    private loadCart() {
        const storedCart = localStorage.getItem('cart');
        if (storedCart) {
            this.itemsMap.set(JSON.parse(storedCart));
        }
        this.updateItemCount();
        this.updateCartItems();
    }

    private updateCartItems() {
        const itemsArray = Object.values(this.itemsMap()).map((item) => ({
            product: item.product,
            amount: item.amount,
            selectedVariant: item.selectedVariant,
            selectedDeliveryDate: item.selectedDeliveryDate,
        }));
        this.cartItems.set(itemsArray);
    }

    private updateItemCount() {
        const totalItems = Object.values(this.itemsMap()).reduce(
            (total, item) => total + item.amount,
            0,
        );
        this.itemCount.set(totalItems);
    }

    private saveAndLoadCart() {
        this.saveCart();
        this.updateCartItems();
        this.updateItemCount();
    }

    private createItemKey(cartItem: CartItem) {
        if (cartItem.selectedDeliveryDate) {
            return `${cartItem.product.slug}-${cartItem.product.documentId}-${cartItem.selectedVariant.uuid}-${cartItem.selectedDeliveryDate.documentId}`;
        }
        return `${cartItem.product.slug}-${cartItem.product.documentId}-${cartItem.selectedVariant.uuid}`;
    }
}

export type CartItemMap = {
    [key: string]: CartItem;
};

export type CartItem = {
    product: Product;
    selectedVariant: Variant;
    selectedDeliveryDate?: DeliveryDate;
    amount: number;
};
